import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Company {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private apiUrl = environment.apiUrl || '/api';
  private cachedCompany: Observable<Company> | null = null;
  private companyEnvDefault = '2M Makina'; // Default from .env COMPANY_NAME

  constructor(private http: HttpClient) {    
    // Force clear the cache on service initialization
    this.clearCache();
    
    // Log auth token status
    console.log('Auth token exists:', !!localStorage.getItem('auth_token'));
  }

  /**
   * Get public company info without authentication
   * This is a fallback for when the user is not authenticated
   */
  getPublicCompanyInfo(): Observable<Company> {
    console.log('Getting public company info...');
    const url = `${this.apiUrl}/company/info`;
    console.log('Public company URL:', url);
    
    return this.http.get<Company>(url)
      .pipe(
        tap(company => console.log('Successfully fetched public company info:', company)),
        catchError(error => {
          console.error('Error fetching public company info:', error);
          // Return a default company object
          return of({ 
            id: 0, 
            name: this.companyEnvDefault || 'HR Panel'
          });
        })
      );
  }

  /**
   * Get current user's company info
   */
  getCurrentCompany(): Observable<Company> {
    console.log('Getting current company info...');
    
    // Always clear cache to ensure fresh data
    this.clearCache();
    
    if (this.cachedCompany) {
      console.log('Returning cached company info');
      return this.cachedCompany;
    }

    // Try to fetch authenticated company info first, fall back to public info
    const url = `${this.apiUrl}/company/current`;
    console.log('Authentication-required company URL:', url);
    
    this.cachedCompany = this.http.get<Company>(url)
      .pipe(
        tap(company => console.log('Successfully fetched authenticated company:', company)),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching company:', error);
          console.error('Status:', error.status);
          console.error('Message:', error.message);
          
          if (error.status === 401) {
            console.error('Authentication error - user not logged in or token expired');
            console.log('Falling back to public company info');
            return this.getPublicCompanyInfo();
          } else if (error.status === 404) {
            console.error('Company not found or not associated with user');
            return this.getPublicCompanyInfo();
          } else if (error.status === 0) {
            console.error('Network error - likely CORS or server unreachable');
            console.log('Server might be down or on different port than expected:', this.apiUrl);
          }
          
          // Clear cache on error and use default
          this.cachedCompany = null;
          return this.getPublicCompanyInfo();
        }),
        // Share the same response with all subscribers
        shareReplay(1)
      );

    return this.cachedCompany;
  }

  /**
   * Clear the company cache to force a refresh on next request
   */
  clearCache(): void {
    this.cachedCompany = null;
  }

  /**
   * Get company name for display
   * Returns a default name if company info can't be fetched
   */
  getCompanyName(): Observable<string> {
    console.log('Getting company name');
    return this.getCurrentCompany().pipe(
      map(company => {
        console.log('Mapping company to name:', company.name);
        return company.name;
      }),
      catchError(error => {
        console.error('Error getting company name:', error);
        return of(this.companyEnvDefault || 'HR Panel'); // Fallback name if company fetch fails
      })
    );
  }

  /**
   * Get company initials for logo (up to 2 characters)
   */
  getCompanyInitials(): Observable<string> {
    console.log('Getting company initials');
    return this.getCompanyName().pipe(
      map(name => {
        console.log('Generating initials from name:', name);
        // Extract initials from company name (maximum 2 characters)
        const words = name.split(/\s+/);
        let initials;
        
        if (words.length === 1) {
          // Single word - get first 2 characters
          initials = name.substring(0, 2).toUpperCase();
        } else {
          // Multiple words - get first character of first two words
          initials = (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
        }
        
        console.log('Generated initials:', initials);
        return initials;
      }),
      catchError(error => {
        console.error('Error generating initials:', error);
        // Generate default initials from the default company name
        const defaultName = this.companyEnvDefault || 'HR';
        const words = defaultName.split(/\s+/);
        let defaultInitials = defaultName.substring(0, 2).toUpperCase();
        if (words.length > 1) {
          defaultInitials = (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
        }
        return of(defaultInitials);
      })
    );
  }
} 